import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiAward, FiTarget } from "react-icons/fi";
import { participantApi } from "../../../api/participant";

const pickLearningPaths = (response) => response?.data || [];
const pickCourses = (response) => response?.data?.courses || [];
const pickActionPlans = (response) => response?.data || response?.resourceData || response || [];
const getActionPlanId = (item) => item?.actionPlanId || item?.id || "";

const Achievements = () => {
  const [searchParams] = useSearchParams();
  const [selectedLearningPath, setSelectedLearningPath] = useState({
    trackerId: "",
    learningPathId: "",
  });

  const [goalForm, setGoalForm] = useState({
    number: 1,
    courseId: "",
    keyLearning: "",
    actions: "",
  });

  const [achievementForm, setAchievementForm] = useState({
    number: 1,
    achievement: "",
  });
  const [goalActionPlanId, setGoalActionPlanId] = useState("");
  const [achievementActionPlanId, setAchievementActionPlanId] = useState("");

  const { data: learningPathRes } = useQuery(
    ["participant-learning-paths-achievement"],
    participantApi.getLearningPaths,
    { staleTime: 60000 }
  );

  const { data: actionPlansRes } = useQuery(
    ["participant-action-plans-achievement"],
    participantApi.getActionPlans,
    { staleTime: 60000 }
  );

  const learningPaths = pickLearningPaths(learningPathRes);
  const actionPlans = Array.isArray(pickActionPlans(actionPlansRes)) ? pickActionPlans(actionPlansRes) : [];

  const { data: learningPathDetailRes } = useQuery(
    ["participant-learning-path-detail-achievement", selectedLearningPath.trackerId, selectedLearningPath.learningPathId],
    () =>
      participantApi.getLearningPathDetail({
        trackerId: selectedLearningPath.trackerId,
        learningPathId: selectedLearningPath.learningPathId,
      }),
    {
      enabled: Boolean(selectedLearningPath.trackerId && selectedLearningPath.learningPathId),
      staleTime: 60000,
    }
  );

  const courses = pickCourses(learningPathDetailRes);
  const selectedActionPlan =
    actionPlans.find((item) => String(getActionPlanId(item)) === String(achievementActionPlanId || "")) || null;

  const completeByDateTime = selectedActionPlan?.completeByDateTime || null;

  const canSubmitAchievement = useMemo(() => {
    if (!completeByDateTime) return true;
    return new Date().getTime() >= new Date(completeByDateTime).getTime();
  }, [completeByDateTime]);

  useEffect(() => {
    const actionPlanIdFromQuery = searchParams.get("actionPlanId");
    const trackerIdFromQuery = searchParams.get("trackerId");
    const learningPathIdFromQuery = searchParams.get("learningPathId");

    if (actionPlanIdFromQuery) {
      setGoalActionPlanId(actionPlanIdFromQuery);
      setAchievementActionPlanId(actionPlanIdFromQuery);
    }

    if (trackerIdFromQuery && learningPathIdFromQuery) {
      setSelectedLearningPath({
        trackerId: trackerIdFromQuery,
        learningPathId: learningPathIdFromQuery,
      });
    }
  }, [searchParams]);

  useEffect(() => {
    if (!selectedLearningPath.trackerId || !selectedLearningPath.learningPathId || !actionPlans.length) return;

    const planForSelectedPath = actionPlans.find((item) => {
      const itemTrackerId = item?.trackerId || item?.cohortParticipantId || item?.participantTrackerId;
      const itemLearningPathId = item?.learningPathId || item?.journeyId;
      return String(itemTrackerId || "") === String(selectedLearningPath.trackerId) &&
        String(itemLearningPathId || "") === String(selectedLearningPath.learningPathId);
    });

    const planId = getActionPlanId(planForSelectedPath);
    if (!planId) return;

    if (!goalActionPlanId) setGoalActionPlanId(planId);
    if (!achievementActionPlanId) setAchievementActionPlanId(planId);
  }, [selectedLearningPath, actionPlans, goalActionPlanId, achievementActionPlanId]);

  useEffect(() => {
    if (!actionPlans.length) return;
    const activePlan = actionPlans.find((item) => String(item?.status || "").toLowerCase() === "active");
    const fallback = activePlan || actionPlans[0];
    if (!fallback) return;
    const fallbackPlanId = getActionPlanId(fallback);
    if (!goalActionPlanId) setGoalActionPlanId(fallbackPlanId);
    if (!achievementActionPlanId) setAchievementActionPlanId(fallbackPlanId);
  }, [actionPlans, goalActionPlanId, achievementActionPlanId]);

  const goalMutation = useMutation(participantApi.submitGoal, {
    onSuccess: (response) => {
      toast.success(response?.message || "Goal submitted successfully.");
      setGoalForm({ number: 1, courseId: "", keyLearning: "", actions: "" });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to submit goal.");
    },
  });

  const achievementMutation = useMutation(participantApi.submitAchievement, {
    onSuccess: (response) => {
      toast.success(response?.message || "Achievement submitted successfully.");
      setAchievementForm({ number: 1, achievement: "" });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to submit achievement.");
    },
  });

  const onSubmitGoal = (event) => {
    event.preventDefault();
    if (!goalActionPlanId) {
      toast.error("Select an action plan first.");
      return;
    }

    goalMutation.mutate({
      actionPlanId: goalActionPlanId,
      goal: {
        number: Number(goalForm.number),
        courseId: goalForm.courseId,
        keyLearning: goalForm.keyLearning,
        actions: goalForm.actions,
      },
    });
  };

  const onSubmitAchievement = (event) => {
    event.preventDefault();
    if (!achievementActionPlanId) {
      toast.error("Select an action plan first.");
      return;
    }

    if (!canSubmitAchievement) {
      toast.error("Achievement submission opens on the action-plan due date.");
      return;
    }

    achievementMutation.mutate({
      actionPlanId: achievementActionPlanId,
      number: Number(achievementForm.number),
      achievement: achievementForm.achievement,
    });
  };

  return (
    <div className="max-w-[1000px]">
      <h1 className="text-3xl font-bold text-[#0A3059] mb-6">Achievement</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <form onSubmit={onSubmitGoal} className="bg-white border border-gray-100 shadow-sm rounded-xl p-5">
          <h2 className="text-xl font-semibold text-[#0A3059] inline-flex items-center gap-2 mb-4">
            <FiTarget /> Submit Goal
          </h2>

          <div className="space-y-3">
            <select
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"
              value={goalActionPlanId}
              onChange={(event) => setGoalActionPlanId(event.target.value)}
              required
            >
              <option value="">Select Action Plan</option>
              {actionPlans.map((item) => (
                <option key={getActionPlanId(item)} value={getActionPlanId(item)}>
                  {item?.name || item?.title || getActionPlanId(item)}
                </option>
              ))}
            </select>
            <input
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
              type="number"
              min="1"
              placeholder="Goal Number"
              value={goalForm.number}
              onChange={(event) => setGoalForm((prev) => ({ ...prev, number: event.target.value }))}
              required
            />

            <select
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"
              value={`${selectedLearningPath.trackerId}::${selectedLearningPath.learningPathId}`}
              onChange={(event) => {
                const [trackerId, learningPathId] = event.target.value.split("::");
                setSelectedLearningPath({ trackerId, learningPathId });
                setGoalForm((prev) => ({ ...prev, courseId: "" }));
              }}
              required
            >
              <option value="::">Select Learning Path</option>
              {learningPaths.map((item) => (
                <option
                  key={`${item?.trackerId}-${item?.learningPathId}`}
                  value={`${item?.trackerId}::${item?.learningPathId}`}
                >
                  {item?.learningPathName}
                </option>
              ))}
            </select>

            <select
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
              value={goalForm.courseId}
              onChange={(event) => setGoalForm((prev) => ({ ...prev, courseId: event.target.value }))}
              required
              disabled={!courses.length}
            >
              <option value="">{courses.length ? "Select Course" : "Select Learning Path First"}</option>
              {courses.map((course) => (
                <option key={course?.courseId} value={course?.courseId}>
                  {course?.courseName}
                </option>
              ))}
            </select>
            <textarea
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm min-h-[100px]"
              placeholder="Key Learning"
              value={goalForm.keyLearning}
              onChange={(event) => setGoalForm((prev) => ({ ...prev, keyLearning: event.target.value }))}
              required
            />
            <textarea
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm min-h-[100px]"
              placeholder="Actions"
              value={goalForm.actions}
              onChange={(event) => setGoalForm((prev) => ({ ...prev, actions: event.target.value }))}
              required
            />

            <button
              type="submit"
              disabled={goalMutation.isLoading}
              className="px-4 py-2 rounded-md text-white bg-[#0A3059] hover:bg-[#082649] text-sm disabled:opacity-70"
            >
              {goalMutation.isLoading ? "Submitting..." : "Submit Goal"}
            </button>
          </div>
        </form>

        <form onSubmit={onSubmitAchievement} className="bg-white border border-gray-100 shadow-sm rounded-xl p-5">
          <h2 className="text-xl font-semibold text-[#0A3059] inline-flex items-center gap-2 mb-4">
            <FiAward /> Submit Achievement
          </h2>

          <div className="space-y-3">
            <select
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"
              value={achievementActionPlanId}
              onChange={(event) => setAchievementActionPlanId(event.target.value)}
              required
            >
              <option value="">Select Action Plan</option>
              {actionPlans.map((item) => (
                <option key={getActionPlanId(item)} value={getActionPlanId(item)}>
                  {item?.name || item?.title || getActionPlanId(item)}
                </option>
              ))}
            </select>
            <input
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
              type="number"
              min="1"
              placeholder="Achievement Number"
              value={achievementForm.number}
              onChange={(event) => setAchievementForm((prev) => ({ ...prev, number: event.target.value }))}
              required
            />
            <textarea
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm min-h-[100px]"
              placeholder="Achievement"
              value={achievementForm.achievement}
              onChange={(event) => setAchievementForm((prev) => ({ ...prev, achievement: event.target.value }))}
              required
            />

            {completeByDateTime ? (
              <p className="text-xs text-gray-500">
                Achievement can be submitted on or after {new Date(completeByDateTime).toLocaleString()}.
              </p>
            ) : null}

            <button
              type="submit"
              disabled={achievementMutation.isLoading || !canSubmitAchievement}
              className="px-4 py-2 rounded-md text-white bg-[#0A3059] hover:bg-[#082649] text-sm disabled:opacity-70"
            >
              {achievementMutation.isLoading ? "Submitting..." : "Submit Achievement"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Achievements;
